/**
 * Notification Bell
 *
 * Wraps the bell icon button with a live unread-count badge. Tap toggles the
 * inbox panel underneath. Shared across desktop + mobile headers so the
 * polling instance lives in exactly one place.
 */

import { Bell } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { useUserNotifications } from '../../hooks/use-user-notifications';
import { NotificationInbox } from './notification-inbox';

interface NotificationBellProps {
  className?: string;
}

export const NotificationBell = ({ className }: NotificationBellProps) => {
  const { t } = useTranslation('navigation');
  const { unreadCount } = useUserNotifications();
  const [isOpen, setIsOpen] = useState(false);

  // Close on Escape when open
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={cn(
          'relative p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors',
          className
        )}
        title={t('header.notifications')}
        aria-label={t('header.notifications')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-error-500 rounded-full ring-2 ring-white"
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationInbox isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};
